import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SupabaseAuthProvider as AuthProvider, useAuth } from './contexts/SupabaseAuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { CartProvider } from './contexts/CartContext';
import { SupabaseDataProvider, useSupabaseData } from './contexts/SupabaseDataContext';
import { SupabaseAdminProvider } from './contexts/SupabaseAdminContext';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HeroSection from './components/sections/HeroSection';
import HonestDisclosureBar from './components/sections/HonestDisclosureBar';
import TrustIndicators from './components/sections/TrustIndicators';
import SEOHead from './components/common/SEOHead';
import { getHomeSEO } from './utils/seo';
import ProductSection from './components/sections/ProductSection';
import IngredientGroupedSection from './components/sections/IngredientGroupedSection';
import WhyLiberaCure from './components/sections/WhyLiberaCure';
import StatsBand from './components/sections/StatsBand';
import PopularKeywords from './components/sections/PopularKeywords';
import { getProductCategory } from './types';
import ProductDetail from './components/pages/ProductDetail';
import Shop from './components/pages/Shop';
import Review from './components/pages/Review';
import ReviewForm from './components/pages/ReviewForm';
import ReviewSection from './components/sections/ReviewSection';
import Login from './components/pages/Login';
import Signup from './components/pages/Signup';
import SignupComplete from './components/pages/SignupComplete';
import AuthCallback from './components/pages/AuthCallback';
import MyPage from './components/pages/MyPage';
import TermsOfService from './components/pages/TermsOfService';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import Notices from './components/pages/Notices';
import TrackingOrder from './components/pages/TrackingOrder';
import Inquiry from './components/pages/Inquiry';
import Checkout from './components/pages/Checkout';
import ContactMethod from './components/pages/ContactMethod';
import OrderComplete from './components/pages/OrderComplete';
import Cart from './components/pages/Cart';
import PaymentGuide from './components/pages/PaymentGuide';
import PolicyPage from './components/pages/PolicyPage';
import ComparisonPage from './components/pages/ComparisonPage';
import Offers from './components/pages/Offers';
import Mission from './components/pages/Mission';
import Honestly from './components/pages/Honestly';
import Guides from './components/pages/Guides';
import GuidesCategory from './components/pages/GuidesCategory';
import GuideArticle from './components/pages/GuideArticle';
import CryptoPayment from './components/pages/CryptoPayment';
import ForgotPassword from './components/pages/ForgotPassword';
import ResetPassword from './components/pages/ResetPassword';

// Admin Components
import AdminLayout from './components/layout/AdminLayout';
import AdminLogin from './components/pages/AdminLogin';
import AdminDashboard from './components/pages/AdminDashboard';
import AdminNotices from './components/pages/AdminNotices';
import AdminNoticeForm from './components/pages/AdminNoticeForm';
import AdminProducts from './components/pages/AdminProducts';
import AdminImageTriage from './components/pages/AdminImageTriage';
import AdminProductForm from './components/pages/AdminProductForm';
import AdminInquiries from './components/pages/AdminInquiries';
import AdminReviews from './components/pages/AdminReviews';
import AdminOrders from './components/pages/AdminOrders';
import AdminCategories from './components/pages/AdminCategories';
import AdminCoupons from './components/pages/AdminCoupons';
import AdminPriceReview from './components/pages/AdminPriceReview';
import AdminImageReview from './components/pages/AdminImageReview';
import AdminProductPicker from './components/pages/AdminProductPicker';
import LoadingSpinner from './components/layout/LoadingSpinner';
import ScrollToTop from './components/common/ScrollToTop';


function AppContent() {
  // Auth state check first
  const { loading: authLoading } = useAuth();
  
  // 🎯 Production-Ready: Single source of truth from Supabase
  const { products, isLoading: dataLoading, error } = useSupabaseData();
  
  // Show loading while verifying authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Verifying authentication..." />
      </div>
    );
  }

  // Error handling for production
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h2 className="font-display text-display-sm text-neutral-950 mb-4">Connection Error</h2>
          <p className="text-body text-neutral-500 mb-6">{error}</p>
          <button
            onClick={() => {
              const btn = document.activeElement as HTMLButtonElement;
              if (btn) btn.disabled = true;
              window.location.reload();
            }}
            className="btn-primary disabled:opacity-50"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Data loading state (after auth is complete)
  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading data..." />
      </div>
    );
  }

  // Extract active ingredient from product name (inside parens), strip dosage
  const extractIngredient = (name: string): string | null => {
    if (!name) return null;
    const m = name.match(/\(([^)]+)\)/);
    if (!m) return null;
    return m[1]
      .replace(/\s*\d+(\.\d+)?\s*(mg|mcg|g|%|ml|iu)\b.*/gi, '')
      .trim()
      .toLowerCase();
  };

  // Per category: group by ingredient, keep the "best" (lowest sort_order) per group.
  // sort_order=10 rows are recently-curated products with quality imagery.
  const pickBestPerIngredient = (list: any[]): any[] => {
    const byIng = new Map<string, any>();
    for (const p of list) {
      const ing = extractIngredient(p.name);
      if (!ing) continue;
      const cur = byIng.get(ing);
      if (!cur || (p.sort_order ?? 999) < (cur.sort_order ?? 999)) {
        byIng.set(ing, p);
      }
    }
    return Array.from(byIng.values())
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  };

  // Homepage sections: prefer admin-picked `is_featured` products.
  // Fallback to best-per-ingredient if nothing is featured yet.
  // `excludeIngredients` drops specific ingredient groups (e.g. Orlistat) before
  // the featured/best-per-ingredient pick so low-intent items don't clutter a
  // section with higher-value cousins.
  const curateForHome = (category: string, opts?: { excludeIngredients?: string[] }) => {
    const excludeIng = (opts?.excludeIngredients || []).map(s => s.toLowerCase());
    let inCat = products.filter(p => ((p as any).categories || []).includes(category));
    if (excludeIng.length > 0) {
      inCat = inCat.filter(p => !excludeIng.includes(extractIngredient(p.name || '') || ''));
    }
    const featured = inCat.filter(p => (p as any).is_featured);
    if (featured.length > 0) return featured.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
    return pickBestPerIngredient(inCat);
  };

  const sexualHealthProducts = curateForHome('sexual-health');
  const hairProducts = curateForHome('hair-loss');
  // Weight section: drop Orlistat (fat-blocker, low-intent here) and keep
  // the GLP-1 / SGLT2 / DPP-4 story clean. Orlistat still shows under
  // /shop/weight-management.
  const weightProducts = curateForHome('weight-management', { excludeIngredients: ['Orlistat'] });
  const skinProducts = curateForHome('skin-care');
  const heartProducts = curateForHome('heart-health');
  const antibioticsProducts = curateForHome('antibiotics');

  const HomePage = () => (
    <main>
      <SEOHead seoData={getHomeSEO()} />
      <HeroSection />
      <HonestDisclosureBar />
      <TrustIndicators />

      <IngredientGroupedSection
        title="Sexual Health"
        subtitle="Effective treatments, delivered discreetly"
        products={sexualHealthProducts}
        viewAllLink="/shop/sexual-health"
      />

      <WhyLiberaCure />

      <IngredientGroupedSection
        title="Weight & Diabetes"
        subtitle="Semaglutide, SGLT2, DPP-4 and beyond"
        products={weightProducts}
        viewAllLink="/shop/weight-management"
        className="bg-neutral-50"
        hideOrphans
      />

      <IngredientGroupedSection
        title="Hair Loss Treatment"
        subtitle="Clinically proven solutions"
        products={hairProducts}
        viewAllLink="/shop/hair-loss"
      />

      <StatsBand />

      <IngredientGroupedSection
        title="Skin Care"
        subtitle="Acne, retinoids, and dermatology essentials"
        products={skinProducts}
        viewAllLink="/shop/skin-care"
      />

      <IngredientGroupedSection
        title="Heart Health"
        subtitle="Cardiovascular care, trusted generics"
        products={heartProducts}
        viewAllLink="/shop/heart-health"
        className="bg-neutral-50"
      />

      <IngredientGroupedSection
        title="Antibiotics"
        subtitle="Prescription antibacterials, antivirals, antifungals"
        products={antibioticsProducts}
        viewAllLink="/shop/antibiotics"
      />

      <ReviewSection />
    </main>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/:category" element={<Shop />} />
        <Route path="/shop/:category/:subcategory" element={<Shop />} />
        <Route path="/review" element={<Review products={products} />} />
        <Route path="/review/write" element={<ReviewForm />} />
        <Route path="/product/:productId" element={<ProductDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-complete" element={<SignupComplete />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/find-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/mypage" element={<MyPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/policy/:slug" element={<PolicyPage />} />
        <Route path="/compare/:slug" element={<ComparisonPage />} />
        <Route path="/notices" element={<Notices />} />
        <Route path="/offers" element={<Offers />} />
        <Route path="/tracking" element={<TrackingOrder />} />
        <Route path="/inquiry" element={<Inquiry />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/contact-method/:orderId" element={<ContactMethod />} />
        <Route path="/crypto-payment/:orderId" element={<CryptoPayment />} />
        <Route path="/order-complete/:orderId" element={<OrderComplete />} />
        <Route path="/payment-guide" element={<PaymentGuide />} />
        <Route path="/mission" element={<Mission />} />
        <Route path="/honestly" element={<Honestly />} />
        <Route path="/why-we-look-sus" element={<Honestly />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/:category" element={<GuidesCategory />} />
        <Route path="/guides/:category/:slug" element={<GuideArticle />} />
      </Routes>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <ConfirmProvider>
      <SupabaseDataProvider>
          <SupabaseAdminProvider>
            <AdminProvider>
              <CartProvider>
                <Router>
                  <ScrollToTop />

                <Routes>
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/*" element={
                    <AdminLayout>
                      <Routes>
                        <Route index element={<AdminDashboard />} />
                        <Route path="notices" element={<AdminNotices />} />
                        <Route path="notices/new" element={<AdminNoticeForm />} />
                        <Route path="notices/:id/edit" element={<AdminNoticeForm />} />
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="products/new" element={<AdminProductForm />} />
                        <Route path="products/:id/edit" element={<AdminProductForm />} />
                        <Route path="image-triage" element={<AdminImageTriage />} />
                        <Route path="inquiries" element={<AdminInquiries />} />
                        <Route path="reviews" element={<AdminReviews />} />
                        <Route path="orders" element={<AdminOrders />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="coupons" element={<AdminCoupons />} />
                        <Route path="price-review" element={<AdminPriceReview />} />
                        <Route path="image-review" element={<AdminImageReview />} />
                        <Route path="product-picker" element={<AdminProductPicker />} />
                      </Routes>
                    </AdminLayout>
                  } />

                  {/* Public Routes */}
                  <Route path="/*" element={<AppContent />} />
                </Routes>
                </Router>
              </CartProvider>
            </AdminProvider>
          </SupabaseAdminProvider>
      </SupabaseDataProvider>
      </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
